import { plainSpeech } from './plainSpeech';

/**
 * FIRST WORDS FAST. A reply is spoken sentence by sentence: the first complete sentence as soon as it has streamed in, the
 * following ones queued through the same mouth in order, ONE synthesis per sentence, none twice. The whole reply's text is
 * still what is on screen, and the FINAL text is the text: if what streamed turns out not to be a prefix of it, the draft
 * is silenced and the final is spoken whole (a draft is never left standing as fact).
 *
 *   const sp = createReplySpeaker({ mouth, key, language, extra, onReveal })
 *   sp.push(delta)          // streamed text: a complete sentence is spoken as soon as it exists
 *   sp.end(finalText)       // → 'done' | 'stopped' | 'failed' | 'skipped' (when every sentence has been heard)
 *   sp.cancel()             // silence it; later push/end speak nothing
 * `onReveal(text)` is the caption: the reply as far as it has begun to be heard (it follows the sound).
 */
const END = /([.?!؟…]+["'”’)\]]*)(\s+)/g;

/** Sentences of a text: split after . ? ! ؟ … when whitespace follows (so 10.30 stays whole), and at line breaks. */
export function splitSentences(text) {
  const out = [];
  for (const line of String(text || '').split(/\n+/)) {
    let last = 0; let m;
    END.lastIndex = 0;
    while ((m = END.exec(line))) {
      const piece = line.slice(last, m.index + m[1].length).trim();
      if (piece) out.push(piece);
      last = m.index + m[0].length;
    }
    const rest = line.slice(last).trim();
    if (rest) out.push(rest);
  }
  return out;
}

const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim();

export function createReplySpeaker({ mouth, key, language: spokenIn, extra = {}, onReveal } = {}) {
  let language = spokenIn;
  let draft = '';
  let queued = []; // the sentences already handed to the mouth, in order
  let cancelled = false;
  const results = [];
  let revealed = '';

  const say = (text, i, total, ownKey) => {
    const first = queued.length === 0;
    queued.push(text);
    // A reply that is one sentence keeps its own key and its whole text, exactly as before; the rest are #i.
    const args = { ...extra, key: total === 1 ? (ownKey ?? key) : `${key}#${i}`, text, language };
    const reveal = () => { revealed = revealed ? `${revealed} ${text}` : text; onReveal?.(revealed); };
    const p = first ? mouth.speak(args, { onStart: reveal }) : (mouth.enqueue ? mouth.enqueue(args, { onStart: reveal }) : Promise.resolve(null).then(() => mouth.speak(args, { onStart: reveal })));
    results.push(p);
    return p;
  };

  return {
    push(delta) {
      if (cancelled) return;
      draft += String(delta || '');
      // What is spoken is the words, not the markdown marks: the draft is read the way the final will be, so they agree.
      const spoken = plainSpeech(draft);
      const parts = splitSentences(spoken);
      // All but the last part are complete (a sentence is complete when whitespace follows its end).
      const closed = /[.?!؟…]["'”’)\]]*\s$/.test(draft) || /\n$/.test(draft);
      const ready = closed ? parts.length : parts.length - 1;
      for (let i = queued.length; i < ready; i++) say(parts[i], i, 2);
    },
    cancel() { cancelled = true; try { mouth.stop(); } catch (_) { /* gone */ } },
    async end(finalText, replyKey, replyLanguage) {
      if (replyLanguage) language = replyLanguage; // the reply says which language it is in; the streamed draft only knew the one spoken
      if (cancelled) return 'stopped';
      const final = plainSpeech(String(finalText || ''));
      if (!final) return results.length ? settle(results) : 'skipped';
      const finalParts = splitSentences(final);
      let from = queued.length;
      if (queued.length) {
        const spokenJoined = norm(queued.join(' '));
        const finalHead = norm(finalParts.slice(0, queued.length).join(' '));
        if (spokenJoined !== finalHead) {
          // What streamed was not the answer. Silence it; speak the final whole.
          try { mouth.stop(); } catch (_) { /* gone */ }
          queued = []; results.length = 0; revealed = ''; from = 0;
        }
      }
      if (from === 0 && finalParts.length === 1) say(final, 0, 1, replyKey);
      else for (let i = from; i < finalParts.length; i++) say(finalParts[i], i, Math.max(2, finalParts.length));
      return settle(results);
    },
  };
}

async function settle(list) {
  const all = await Promise.all(list);
  if (all.includes('stopped')) return 'stopped';
  if (all.length && all.every((r) => r === 'failed')) return 'failed';
  if (all.length && all.every((r) => r === 'skipped')) return 'skipped';
  return 'done';
}
